import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, PlayCircle, Film, X, CloudDownload, FileText, GripVertical, Eye } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CHUNK = 4 * 1024 * 1024;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOC_EXTS = ["pptx", "ppt", "pdf", "docx", "doc", "xlsx", "xls"];

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1000 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

function SlotRow({ slot, busy, onUpload, onRemove, libEntry, adopting, onAdopt, docs, docBusy, onUploadDoc, onRemoveDoc, analytics, onReorderDoc }) {
  const fileRef = useRef(null);
  const docRef = useRef(null);
  const dragIdRef = useRef(null);
  const [preview, setPreview] = useState(false);
  const v = slot.video;
  const canAdopt = libEntry && (!v || v.id !== libEntry.id);
  const vWatch = analytics?.videos?.[slot.key] || 0;
  return (
    <div className="rounded-xl border border-black/10 bg-white p-4" data-testid={`video-slot-${slot.key}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-900 flex items-center gap-2">
            {slot.label}
            {vWatch > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5"
                    title={`${vWatch} trainee${vWatch === 1 ? "" : "s"} finished this video`} data-testid={`video-watch-count-${slot.key}`}>
                <Eye size={10} /> {vWatch}
              </span>
            )}
          </p>
          {v ? (
            <p className="text-xs text-emerald-700 mt-0.5 truncate">
              {v.original_filename} · {fmtSize(v.size)} · uploaded {new Date(v.created_at).toLocaleDateString()}
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5">No custom video — YouTube fallback plays if one exists</p>
          )}
          {canAdopt && (
            <p className="text-xs text-indigo-600 mt-0.5 truncate" data-testid={`video-library-hint-${slot.key}`}>
              In cloud library: {libEntry.filename} · {fmtSize(libEntry.size)} — load it without re-uploading
            </p>
          )}
        </div>
        {busy != null ? (
          <div className="flex items-center gap-2 text-xs text-indigo-700 font-medium">
            <Loader2 size={14} className="animate-spin" /> {busy}%
          </div>
        ) : (
          <div className="flex gap-1.5">
            {canAdopt && (
              <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      disabled={!!adopting} data-testid={`video-adopt-${slot.key}`} onClick={() => onAdopt(slot.key)}>
                {adopting ? <Loader2 size={14} className="animate-spin mr-1" /> : <CloudDownload size={14} className="mr-1" />} Load from library
              </Button>
            )}
            {v && (
              <Button size="sm" variant="outline" data-testid={`video-preview-${slot.key}`} onClick={() => setPreview((p) => !p)}>
                {preview ? <X size={14} /> : <PlayCircle size={14} />}
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid={`video-upload-${slot.key}`} onClick={() => fileRef.current?.click()}>
              <Upload size={14} className="mr-1" /> {v ? "Replace" : "Upload"}
            </Button>
            {v && (
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`video-remove-${slot.key}`} onClick={() => onRemove(v.id)}>
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        )}
        <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(slot.key, f); }} />
      </div>
      {busy != null && (
        <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-indigo-600 transition-all" style={{ width: `${busy}%` }} />
        </div>
      )}
      {preview && v && (
        <video controls playsInline preload="metadata" className="w-full aspect-video rounded-lg mt-3 bg-black"
               src={`${BACKEND}/api/videos/${v.id}/stream?auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`} />
      )}

      <div className="mt-3 border-t border-black/5 pt-3" data-testid={`docs-section-${slot.key}`}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <FileText size={12} /> PowerPoints &amp; documents
            {docs && docs.length > 1 && (
              <span className="text-[10px] font-normal text-slate-400" data-testid={`docs-reorder-hint-${slot.key}`}>· drag to reorder</span>
            )}
          </p>
          {docBusy != null ? (
            <span className="text-xs text-indigo-700 font-medium flex items-center gap-1" data-testid={`doc-progress-${slot.key}`}>
              <Loader2 size={12} className="animate-spin" /> {docBusy}%
            </span>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`doc-upload-${slot.key}`}
                    onClick={() => docRef.current?.click()}>
              <Upload size={12} className="mr-1" /> Add PowerPoint
            </Button>
          )}
          <input ref={docRef} type="file" accept=".pptx,.ppt,.pdf,.docx,.doc,.xlsx,.xls" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUploadDoc(slot.key, f); }} />
        </div>
        {docs && docs.length ? (
          <ul className="space-y-1.5">
            {docs.map((d) => {
              const opens = analytics?.docs?.[d.id] || 0;
              return (
              <li key={d.id}
                  draggable
                  onDragStart={() => { dragIdRef.current = d.id; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragIdRef.current;
                    dragIdRef.current = null;
                    if (from && from !== d.id) onReorderDoc(slot.key, from, d.id);
                  }}
                  className="flex items-center gap-2 rounded-lg border border-black/5 bg-slate-50 px-2.5 py-1.5 cursor-move" data-testid={`doc-row-${d.id}`}>
                <GripVertical size={13} className="text-slate-400 shrink-0" data-testid={`doc-drag-${d.id}`} />
                <FileText size={13} className="text-indigo-600 shrink-0" />
                <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">
                  {d.display_name || d.filename}{d.size ? ` · ${fmtSize(d.size)}` : ""}
                </span>
                {opens > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5"
                        title={`Opened by ${opens} trainee${opens === 1 ? "" : "s"}`} data-testid={`doc-open-count-${d.id}`}>
                    <Eye size={10} /> {opens}
                  </span>
                )}
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`doc-remove-${d.id}`} onClick={() => onRemoveDoc(d.id)}>
                  <Trash2 size={12} />
                </Button>
              </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-slate-400" data-testid={`docs-empty-${slot.key}`}>No PowerPoints attached to this topic yet.</p>
        )}
      </div>
    </div>
  );
}

export default function TrainingVideosModal({ open, onOpenChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [library, setLibrary] = useState({});
  const [adopting, setAdopting] = useState(null);
  const [docs, setDocs] = useState({});
  const [docBusy, setDocBusy] = useState({});
  const [analytics, setAnalytics] = useState({ videos: {}, docs: {}, trainees: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/videos");
      setSlots(data.slots || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load videos");
    } finally { setLoading(false); }
    try {
      const { data } = await api.get("/admin/videos/library");
      setLibrary(data.library || {});
    } catch { /* library optional */ }
    try {
      const { data } = await api.get("/admin/videos/docs");
      const grouped = {};
      (data.docs || []).forEach((d) => { (grouped[d.slot] = grouped[d.slot] || []).push(d); });
      setDocs(grouped);
    } catch { /* docs optional */ }
    try {
      const { data } = await api.get("/admin/videos/analytics");
      setAnalytics({ videos: data.videos || {}, docs: data.docs || {}, trainees: data.trainees || 0 });
    } catch { /* analytics optional */ }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const reorderDoc = async (slotKey, fromId, toId) => {
    const list = (docs[slotKey] || []).slice();
    const fromIdx = list.findIndex((d) => d.id === fromId);
    const toIdx = list.findIndex((d) => d.id === toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setDocs((d) => ({ ...d, [slotKey]: list }));  // optimistic
    try {
      await api.post("/admin/videos/docs/reorder", { slot: slotKey, ordered_ids: list.map((d) => d.id) });
      toast.success("Order saved — trainees will see this order");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reorder failed");
      await load();
    }
  };

  const adopt = async (slotKey) => {
    setAdopting(slotKey);
    try {
      await api.post("/admin/videos/adopt", { slot: slotKey });
      toast.success("Video loaded from the cloud library — no upload needed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load from the library");
    } finally { setAdopting(null); }
  };

  const upload = async (slotKey, file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["mp4", "webm", "mov", "m4v"].includes(ext)) { toast.error("Only .mp4, .webm, .mov or .m4v files"); return; }
    if (file.size > 2_000_000_000) { toast.error("File must be under 2 GB"); return; }
    setBusy((b) => ({ ...b, [slotKey]: 0 }));
    try {
      const { data: init } = await api.post("/admin/videos/upload/init", {
        filename: file.name, content_type: file.type || "video/mp4", total_size: file.size, slot: slotKey,
      });
      let sent = 0, index = 0, chunkSize = CHUNK;
      while (sent < file.size) {
        const blob = file.slice(sent, sent + chunkSize);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(index));
        fd.append("chunk", blob, "chunk.bin");
        try {
          await api.post("/admin/videos/upload/chunk", fd);
        } catch (err) {
          const status = err.response?.status;
          if (status === 409) {
            // Backend already received this chunk (response was lost) — move on.
            sent += blob.size; index += 1;
            continue;
          }
          // Proxy rejected the request size / connection dropped — retry the same
          // offset with smaller chunks (production ingress limits are stricter).
          const retriable = !status || [413, 502, 520, 524].includes(status);
          if (retriable && chunkSize > 256 * 1024) {
            chunkSize = Math.max(256 * 1024, Math.floor(chunkSize / 4));
            continue;
          }
          throw err;
        }
        sent += blob.size; index += 1;
        setBusy((b) => ({ ...b, [slotKey]: Math.min(95, Math.round((sent / file.size) * 95)) }));
      }
      setBusy((b) => ({ ...b, [slotKey]: 98 }));
      await api.post("/admin/videos/upload/complete", { upload_id: init.upload_id });
      toast.success("Video uploaded — trainees will see it immediately");
      await load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(detail ? `Upload failed: ${detail}` : status ? `Upload failed (HTTP ${status}) — please retry` : "Upload failed (network) — please retry");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[slotKey]; return n; });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/videos/${id}`);
      toast.success("Video removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const uploadDoc = async (slotKey, file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!DOC_EXTS.includes(ext)) { toast.error("Only PowerPoint, PDF, Word or Excel files"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File must be under 25 MB"); return; }
    setDocBusy((b) => ({ ...b, [slotKey]: 0 }));
    try {
      const fd = new FormData();
      fd.append("slot", slotKey);
      fd.append("file", file, file.name);
      await api.post("/admin/videos/docs", fd, {
        timeout: 180000,
        onUploadProgress: (e) => {
          if (e.total) setDocBusy((b) => ({ ...b, [slotKey]: Math.min(98, Math.round((e.loaded / e.total) * 98)) }));
        },
      });
      toast.success("PowerPoint added — trainees will see it in this topic");
      await load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(detail ? `Upload failed: ${detail}` : status ? `Upload failed (HTTP ${status}) — please retry` : "Upload failed (network) — please retry");
    } finally {
      setDocBusy((b) => { const n = { ...b }; delete n[slotKey]; return n; });
    }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/admin/videos/docs/${docId}`);
      toast.success("PowerPoint removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="training-videos-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Film size={18} /> Training videos</DialogTitle>
          <DialogDescription>
            Upload your own video files (mp4/webm/mov, up to 2 GB) — they play privately inside the onboarding,
            with no YouTube branding or share links. Videos already uploaded from any environment sit in the
            shared cloud library — use &quot;Load from library&quot; to attach them here without re-uploading.
            {analytics.trainees > 0 && (
              <span className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-700 font-medium" data-testid="analytics-summary">
                <Eye size={12} /> Engagement across {analytics.trainees} real trainee{analytics.trainees === 1 ? "" : "s"} — counts show below.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && !slots.length ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-2.5">
            {slots.map((s) => (
              <SlotRow key={s.key} slot={s} busy={busy[s.key]} onUpload={upload} onRemove={remove}
                       libEntry={library[s.key]} adopting={adopting === s.key} onAdopt={adopt}
                       docs={docs[s.key] || []} docBusy={docBusy[s.key]} onUploadDoc={uploadDoc} onRemoveDoc={removeDoc}
                       analytics={analytics} onReorderDoc={reorderDoc} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
